// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import _Store from '@/store'

// MODULE ROUTES (unchanged for now; still top-level)
import Login from '@/router/modules/login.js'
import Countries from '@/router/modules/countries.js'
import States from '@/router/modules/states.js'
import Cities from '@/router/modules/cities.js'
import Languages from '@/router/modules/languages.js'
import Colors from '@/router/modules/colors.js'
import Vehicle_brands from '@/router/modules/vehicleBrands.js'
import Vehicle_models from '@/router/modules/vehicleModels.js'
import Preferences from '@/router/modules/preferences.js'
import Categories from '@/router/modules/categories.js'
import Amenities from '@/router/modules/amenities.js'
import Highlights from '@/router/modules/highlights.js'
import ItineraryCategories from '@/router/modules/itineraryCategories.js'
import BestPlaces from '@/router/modules/best_places.js'
import PartnerCategories from '@/router/modules/partnerCategories.js'
import Services from '@/router/modules/services.js'
import Skills from '@/router/modules/skills.js'
import Faqs from '@/router/modules/faqs.js'
import Faq_sections from '@/router/modules/faqSections.js'
import Users from '@/router/modules/users.js'
import Travellers from '@/router/modules/travellers.js'
import Banks from '@/router/modules/banks.js'
import Local_guides from '@/router/modules/localGuides.js'
import Documents from '@/router/modules/documents.js'
import SupportChat from '@/router/modules/supportChat.js'
import Partners from '@/router/modules/partners.js'
import ExperienceCategories from '@/router/modules/experiencesCategories.js'
import ExperienceDuration from '@/router/modules/experiencesDurations.js'
import Experiences from '@/router/modules/experiences.js'
import Currencies from '@/router/modules/currencies.js'
import Reservations from '@/router/modules/reservations.js'
import Reports from '@/router/modules/reports.js'

// STATIC VIEWS
import ProfileDashboard from '@/components/ProfileDashboard.vue'
import Page404 from '@/components/Page404.vue'

// ⬇️ Admin shell + Dashboard view
import TdashboardShell from '@/templates/Tdashboard.vue'
const AdminDashboard = () => import('@/views/AdminDashboard.vue')
import CommissionSettings from '@/router/modules/commissionSettings.js'
import CustomerLeads from '@/router/modules/customerLeads.js'
import Payouts from '@/router/modules/payouts.js'


const routes = [
  // Public/auth routes
  ...Login,

  // Top-level module routes (still outside shell for now)
  ...Countries,
  ...States,
  ...Cities,
  ...Languages,
  ...Colors,
  ...Vehicle_brands,
  ...Vehicle_models,
  ...Preferences,
  ...Categories,
  ...Amenities,
  ...Highlights,
  ...ItineraryCategories,
  ...BestPlaces,
  ...PartnerCategories,
  ...Services,
  ...Skills,
  ...Faqs,
  ...Faq_sections,
  ...Users,
  ...Travellers,
  ...Banks,
  ...Local_guides,
  ...Documents,
  ...SupportChat,
  ...Partners,
  ...ExperienceCategories,
  ...ExperienceDuration,
  ...Experiences,
  ...Currencies,
  ...Reservations,
  ...Reports,
  ...CommissionSettings,
  ...CustomerLeads,
  ...Payouts,

  // ⬇️ Admin area with SHELL + children
  {
    path: '/admin',
    component: TdashboardShell,          // header + sidebar shell
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'dashboard' } },  // default: /admin -> /admin/dashboard
      {
        path: 'dashboard', name: 'dashboard', component: AdminDashboard,
        beforeEnter: () => { _Store.dispatch('prefetchDashboard') }
      },
      // ⚠️ Later you can move other admin pages in here as children so they also use the shell:
      // { path: 'countries', name: 'countries', component: () => import('@/views/Countries.vue') },
    ]
  },

  // Root: send logged-in users to the right place
  {
    path: '/',
    name: 'root',
    async beforeEnter (to, from, next) {
      const session = await _Store.dispatch('existSession')
      if (!session) return next({ name: 'login' })
      const user = _Store.getters.getUser
      // Admins straight into the shell’s dashboard; partners to experiences
      return next(
        user?.rol_name === 'administrator'
          ? { path: '/admin/dashboard' }
          : { name: 'experiences' }
      )
    }
  },
  // Profile (outside shell, unchanged)
  {
    path: '/profile',
    name: 'profile',
    component: ProfileDashboard,
    meta: { requiresAuth: true }
  },

  // 404
  { path: '/:pathMatch(.*)*', name: 'notfound', component: Page404 }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  const session = await _Store.dispatch('existSession')
  const user = session ? _Store.getters.getUser : null

  const publicPages = ['login', 'recovery', 'forgot-password']

  // If not logged in → login
  if (!session && !publicPages.includes(to.name)) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  if (session && user) {
    // Partners default to experiences
    if (user.rol_name === 'partner' && to.name !== 'experiences') {
      return next({ name: 'experiences' })
    }
    // Admins hitting login → dashboard
    if (to.name === 'login' && user.rol_name === 'administrator') {
      return next({ path: '/admin/dashboard' })
    }
  }

  next()
})

export default router
